import html2pdf from 'html2pdf.js';

const rewriteToWsrv = (rawUrl: string) =>
  `https://wsrv.nl/?url=${encodeURIComponent(rawUrl)}`;

// Wait for all <img> in a root to settle (load or error), including already-complete images
async function waitForImages(root: HTMLElement, timeoutMsPerImage = 8000) {
  const imgs = Array.from(root.querySelectorAll<HTMLImageElement>('img'));
  const promises = imgs.map((img, idx) => new Promise<void>((resolve) => {
    // If the image is already complete (loaded OR failed), resolve immediately
    if (img.complete) {
      return resolve();
    }
    const onDone = () => {
      img.removeEventListener('load', onDone);
      img.removeEventListener('error', onDone);
      resolve();
    };
    img.addEventListener('load', onDone, { once: true });
    img.addEventListener('error', onDone, { once: true });
    // Safety timeout per image so we never hang
    setTimeout(() => {
      // eslint-disable-next-line no-console
      console.warn('[QuotationPDF] image wait timeout', { idx, src: img.currentSrc || img.src });
      onDone();
    }, timeoutMsPerImage);
  }));
  await Promise.all(promises);
}

// Rewrite only off-origin images to CORS-friendly URLs (wsrv), and ensure canvas safety
function rewriteExternalImages(root: HTMLElement) {
  const restored = new Map<HTMLImageElement, { src: string; srcset: string }>();
  const imgs = Array.from(root.querySelectorAll<HTMLImageElement>('img'));

  for (const img of imgs) {
    // Skip data/blob URLs
    const originalSrc = img.currentSrc || img.src;
    if (!originalSrc || originalSrc.startsWith('data:') || originalSrc.startsWith('blob:')) continue;

    try {
      /* eslint-disable no-restricted-globals */
      const u = new URL(originalSrc, location.href);
      // Same-origin is fine: just set crossOrigin + eager
      if (u.origin === location.origin) {
        img.crossOrigin = 'anonymous';
        img.loading = 'eager';
        continue;
      }

      // Off-origin: rewrite via wsrv
      restored.set(img, { src: img.src, srcset: img.srcset });
      img.crossOrigin = 'anonymous'; // must be set before setting src
      img.loading = 'eager';
      img.srcset = '';               // prevent browser picking a different URL
      img.src = rewriteToWsrv(originalSrc);
    } catch {
      // Malformed URL—ignore
    }
  }

  return () => {
    // Restore originals after PDF is generated
    for (const [img, { src, srcset }] of Array.from(restored.entries())) {
      img.srcset = srcset;
      img.src = src;
    }
  };
}

export async function generatePdf(element: HTMLElement, filename: string) {
  // 1) Rewrite off-origin images to a CORS-friendly CDN (no server code needed)
  const tStart = (typeof performance !== 'undefined' ? performance.now() : Date.now());
  const imgCount = element.querySelectorAll('img').length;
  console.log('[QuotationPDF] generatePdf: start', { filename, imgCount });
  const restore = rewriteExternalImages(element);

  // 2) Wait until images are loaded
  const tWait0 = (typeof performance !== 'undefined' ? performance.now() : Date.now());
  await waitForImages(element);
  const tWait1 = (typeof performance !== 'undefined' ? performance.now() : Date.now());
  console.log('[QuotationPDF] generatePdf: images loaded', { durationMs: Math.round(tWait1 - tWait0) });

  // 3) Render to PDF with non-tainting settings
  const tRender0 = (typeof performance !== 'undefined' ? performance.now() : Date.now());
  await html2pdf()
    .set({
      margin: 0,
      filename,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        allowTaint: false,
        logging: false,
        backgroundColor: '#ffffff',
      },
      jsPDF: { unit: 'pt', format: 'a4', orientation: 'portrait' },
    })
    .from(element)
    .save();
  const tRender1 = (typeof performance !== 'undefined' ? performance.now() : Date.now());
  console.log('[QuotationPDF] generatePdf: rendered+saved', { durationMs: Math.round(tRender1 - tRender0) });

  // 4) Put the DOM back
  restore();
  const tEnd = (typeof performance !== 'undefined' ? performance.now() : Date.now());
  console.log('[QuotationPDF] generatePdf: done', { totalMs: Math.round(tEnd - tStart) });
}

// Generate a PDF Blob and download it as a side effect.
// Useful when you need the file content (for upload) and also want to download.
export async function generatePdfBlob(element: HTMLElement, filename: string): Promise<Blob> {
  const tStart = (typeof performance !== 'undefined' ? performance.now() : Date.now());
  const imgCount = element.querySelectorAll('img').length;
  console.log('[QuotationPDF] generatePdfBlob: start', { filename, imgCount });
  const restore = rewriteExternalImages(element);
  const tWait0 = (typeof performance !== 'undefined' ? performance.now() : Date.now());
  await waitForImages(element);
  const tWait1 = (typeof performance !== 'undefined' ? performance.now() : Date.now());
  console.log('[QuotationPDF] generatePdfBlob: images loaded', { durationMs: Math.round(tWait1 - tWait0) });

  // Use the lower-level API to access jsPDF instance, save, and also get Blob
  const tRender0 = (typeof performance !== 'undefined' ? performance.now() : Date.now());
  const worker = html2pdf().set({
    margin: 0,
    filename,
    image: { type: 'jpeg', quality: 0.98 },
    html2canvas: {
      scale: 2,
      useCORS: true,
      allowTaint: false,
      logging: false,
      backgroundColor: '#ffffff',
    },
    jsPDF: { unit: 'pt', format: 'a4', orientation: 'portrait' },
  }).from(element).toPdf();

  const pdf: any = await (worker as any).get('pdf');
  console.log('[QuotationPDF] generatePdfBlob: got pdf instance');
  // Trigger download
  pdf.save(filename);
  // Return blob for upload
  const blob: Blob = pdf.output('blob');
  const tRender1 = (typeof performance !== 'undefined' ? performance.now() : Date.now());
  console.log('[QuotationPDF] generatePdfBlob: rendered', { durationMs: Math.round(tRender1 - tRender0), sizeBytes: (blob as any)?.size });

  restore();
  const tEnd = (typeof performance !== 'undefined' ? performance.now() : Date.now());
  console.log('[QuotationPDF] generatePdfBlob: done', { totalMs: Math.round(tEnd - tStart) });
  return blob;
}

export function downloadJson(data: any, filename: string) {
  const json = JSON.stringify(data, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
